import React, { useEffect, useState } from "react";
import { FiImage, FiPlus, FiSave, FiTrash2, FiUpload, FiUser } from "react-icons/fi";
import Sidebar from "../components/SideBar";
import SEO from "../components/SEO";
import "./HomePage.css";
import { supabase } from "../supabaseClient";

const TABS = [
  "Hero Section",
  "Services",
  "Why This App",
  "AR Section",
  "Reviews",
  "FAQs",
];

function Stars({ count }) {
  return (
    <div className="home-stars" aria-hidden>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < count ? "on" : ""}>★</span>
      ))}
    </div>
  );
}

function HeroSection() {
  const [recordId, setRecordId] = useState(null);
  const [mainTitle, setMainTitle] = useState("");
  const [description, setDescription] = useState("");
  const [downloadText, setDownloadText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const getData = async () => {
      const res = await supabase.from("Home").select("*");
      if (res.data && res.data.length > 0) {
        const row = res.data[0];
        setRecordId(row.id);
        setMainTitle(row.main_title ?? "");
        setDescription(row.description ?? "");
        setDownloadText(row.button_text ?? "");
        setImageUrl(row.image_url ?? "");
      }
    };
    getData();
  }, []);

  const saveHero = async () => {
    if (recordId) {
      await supabase.from("Home").update({ main_title: mainTitle, description, button_text: downloadText }).eq("id", recordId);
    } else {
      const res = await supabase.from("Home").insert([{ main_title: mainTitle, description, button_text: downloadText }]).select();
      setRecordId(res.data[0].id);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fileName = `hero-${Date.now()}-${file.name}`;
    await supabase.storage.from("images").upload(fileName, file);
    const { data } = supabase.storage.from("images").getPublicUrl(fileName);
    const url = data.publicUrl;
    setImageUrl(url);
    if (recordId) {
      await supabase.from("Home").update({ image_url: url }).eq("id", recordId);
    }
    setUploading(false);
  };

  return (
    <div className="home-content">
      <section className="home-card">
        <h2>Hero Image</h2>
        <div className="home-upload">
          <div className="home-upload__box home-upload__box--phone" aria-hidden>
            {imageUrl ? <img src={imageUrl} alt="hero" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <FiImage />}
          </div>
          <div className="home-upload__info">
            <p>Upload hero section phone mockup (recommended: 400×800px)</p>
            <label className="home-btn" style={{ cursor: "pointer" }}>
              <FiUpload aria-hidden />
              {uploading ? "Uploading…" : "Upload Image"}
              <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
            </label>
          </div>
        </div>
      </section>

      <section className="home-card">
        <h2>Hero Content</h2>
        <div className="home-field">
          <label className="home-label" htmlFor="hero-title">Main Title</label>
          <input id="hero-title" type="text" className="home-input" value={mainTitle} onChange={(e) => setMainTitle(e.target.value)} />
        </div>
        <div className="home-field">
          <label className="home-label" htmlFor="hero-desc">Description</label>
          <textarea id="hero-desc" className="home-textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="home-field">
          <label className="home-label" htmlFor="hero-download">Download Button Text</label>
          <input id="hero-download" type="text" className="home-input" value={downloadText} onChange={(e) => setDownloadText(e.target.value)} />
        </div>
        <div className="home-card__foot" style={{ marginTop: "1rem" }}>
          <button type="button" className="home-btn" onClick={saveHero}>
            <FiSave aria-hidden /> Save
          </button>
        </div>
      </section>
    </div>
  );
}

function ServicesSection() {
  const [services, setServices] = useState([]);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [iconSvgName, setIconSvgName] = useState("");

  useEffect(() => {
    const getData = async () => {
      const res = await supabase.from("HomeTwo").select("*").order("sort_order");
      setServices(res.data);
    };
    getData();
  }, []);

  const addService = async () => {
    const res = await supabase.from("HomeTwo").insert([{ title: "New Service", description: "", icon_svg_name: "", sort_order: services.length + 1 }]).select();
    setServices((prev) => [...prev, res.data[0]]);
  };

  const updateService = async (id, field, value) => {
    setServices((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
    await supabase.from("HomeTwo").update({ [field]: value }).eq("id", id);
  };

  const removeService = async (id) => {
    await supabase.from("HomeTwo").delete().eq("id", id);
    setServices((prev) => prev.filter((s) => s.id !== id));
  };

  return (
    <div className="home-content">
      <section className="home-card">
        <div className="home-card__head">
          <h2>Service Cards</h2>
          <button type="button" className="home-btn" onClick={addService}>
            <FiPlus aria-hidden />
            Add Service
          </button>
        </div>
        <div className="home-service-list">
          {services.map((s) => (
            <article key={s.id} className="home-service-item">
              <div className="home-service-item__top">
                <span className="home-service-item__icon" aria-hidden>{s.icon_svg_name}</span>
                <button type="button" className="home-del-btn" aria-label="Delete service" onClick={() => removeService(s.id)}>
                  <FiTrash2 />
                </button>
              </div>
              <div className="home-field">
                <label className="home-label">Title</label>
                <input type="text" className="home-input" value={s.title} onChange={(e) => updateService(s.id, "title", e.target.value)} />
              </div>
              <div className="home-field">
                <label className="home-label">Description</label>
                <textarea className="home-textarea home-textarea--sm" value={s.description} onChange={(e) => updateService(s.id, "description", e.target.value)} />
              </div>
              <div className="home-field">
                <label className="home-label">Icon SVG Name</label>
                <input type="text" className="home-input home-input--sm" value={s.icon_svg_name ?? ""} onChange={(e) => updateService(s.id, "icon_svg_name", e.target.value)} />
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function WhyThisAppSection() {
  const [recordId, setRecordId] = useState(null);
  const [sectionTitle, setSectionTitle] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    const getData = async () => {
      const res = await supabase.from("HomeThree").select("*").eq("section_name", "why_this_app");
      if (res.data && res.data.length > 0) {
        setRecordId(res.data[0].id);
        setSectionTitle(res.data[0].content_data?.title ?? "Why this app?");
        setContent(res.data[0].content_data?.content ?? "");
      }
    };
    getData();
  }, []);

  const save = async () => {
    const payload = { section_name: "why_this_app", content_data: { title: sectionTitle, content } };
    if (recordId) {
      await supabase.from("HomeThree").update(payload).eq("id", recordId);
    } else {
      const res = await supabase.from("HomeThree").insert([payload]).select();
      setRecordId(res.data[0].id);
    }
  };

  return (
    <div className="home-content">
      <section className="home-card">
        <h2>Section Content</h2>
        <div className="home-field">
          <label className="home-label" htmlFor="why-title">Section Title</label>
          <input id="why-title" type="text" className="home-input" value={sectionTitle} onChange={(e) => setSectionTitle(e.target.value)} />
        </div>
        <div className="home-field">
          <label className="home-label" htmlFor="why-content">Content</label>
          <textarea id="why-content" className="home-textarea home-textarea--lg" value={content} onChange={(e) => setContent(e.target.value)} />
        </div>
        <div className="home-card__foot" style={{ marginTop: "1rem" }}>
          <button type="button" className="home-btn" onClick={save}>
            <FiSave aria-hidden /> Save
          </button>
        </div>
      </section>
    </div>
  );
}

function ARSection() {
  const [recordId, setRecordId] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const getData = async () => {
      const res = await supabase.from("HomeThree").select("*").eq("section_name", "ar_section");
      if (res.data && res.data.length > 0) {
        setRecordId(res.data[0].id);
        setImageUrl(res.data[0].image_url ?? "");
      }
    };
    getData();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fileName = `ar-${Date.now()}-${file.name}`;
    await supabase.storage.from("images").upload(fileName, file);
    const { data } = supabase.storage.from("images").getPublicUrl(fileName);
    const url = data.publicUrl;
    setImageUrl(url);
    if (recordId) {
      await supabase.from("HomeThree").update({ image_url: url }).eq("id", recordId);
    } else {
      const res = await supabase.from("HomeThree").insert([{ section_name: "ar_section", image_url: url }]).select();
      setRecordId(res.data[0].id);
    }
    setUploading(false);
  };

  return (
    <div className="home-content">
      <section className="home-card">
        <h2>AR Section Image</h2>
        <div className="home-upload">
          <div className="home-upload__box home-upload__box--wide" aria-hidden>
            {imageUrl ? <img src={imageUrl} alt="AR" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <FiImage />}
          </div>
          <div className="home-upload__info">
            <p>Upload AR section image (recommended: 1200×600px)</p>
            <label className="home-btn" style={{ cursor: "pointer" }}>
              <FiUpload aria-hidden />
              {uploading ? "Uploading…" : "Upload Image"}
              <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
            </label>
          </div>
        </div>
      </section>
    </div>
  );
}

function ReviewsSection() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const getData = async () => {
      const res = await supabase.from("HomeFour").select("*").order("display_order");
      setReviews(res.data);
    };
    getData();
  }, []);

  const addReview = async () => {
    const res = await supabase.from("HomeFour").insert([{ review_text: "", rating: 5, display_order: reviews.length + 1 }]).select();
    setReviews((prev) => [...prev, res.data[0]]);
  };

  const updateReview = async (id, field, value) => {
    setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    await supabase.from("HomeFour").update({ [field]: value }).eq("id", id);
  };

  const removeReview = async (id) => {
    await supabase.from("HomeFour").delete().eq("id", id);
    setReviews((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <div className="home-content">
      <section className="home-card">
        <div className="home-card__head">
          <h2>Review Cards</h2>
          <button type="button" className="home-btn" onClick={addReview}>
            <FiPlus aria-hidden />
            Add Review
          </button>
        </div>
        <div className="home-reviews-grid">
          {reviews.map((r) => (
            <article key={r.id} className="home-review-item">
              <button type="button" className="home-del-btn home-del-btn--corner" aria-label="Delete review" onClick={() => removeReview(r.id)}>
                <FiTrash2 />
              </button>
              <div className="home-review-item__photo-wrap">
                <div className="home-review-item__photo" aria-hidden>
                  <FiUser />
                </div>
              </div>
              <div className="home-field">
                <label className="home-label">Review Text</label>
                <textarea className="home-textarea home-textarea--sm" placeholder="Enter review text..." value={r.review_text} onChange={(e) => updateReview(r.id, "review_text", e.target.value)} />
              </div>
              <div className="home-field">
                <label className="home-label">Rating (1-5)</label>
                <input type="number" min="1" max="5" className="home-input home-input--rating" value={r.rating} onChange={(e) => updateReview(r.id, "rating", Math.min(5, Math.max(1, Number(e.target.value) || 1)))} />
                <Stars count={r.rating} />
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function FAQsSection() {
  const [recordId, setRecordId] = useState(null);
  const [sectionTitle, setSectionTitle] = useState("");
  const [testimonialImage, setTestimonialImage] = useState("");
  const [personName, setPersonName] = useState("");
  const [testimonialQuote, setTestimonialQuote] = useState("");

  useEffect(() => {
    const getData = async () => {
      const res = await supabase.from("HomeFive").select("*");
      if (res.data && res.data.length > 0) {
        const row = res.data[0];
        setRecordId(row.id);
        setSectionTitle(row.section_title ?? "");
        setTestimonialImage(row.testimonial_image ?? "");
        setPersonName(row.person_name ?? "");
        setTestimonialQuote(row.testimonial_quote ?? "");
      }
    };
    getData();
  }, []);

  const save = async () => {
    const payload = { section_title: sectionTitle, testimonial_image: testimonialImage, person_name: personName, testimonial_quote: testimonialQuote };
    if (recordId) {
      await supabase.from("HomeFive").update(payload).eq("id", recordId);
    } else {
      const res = await supabase.from("HomeFive").insert([payload]).select();
      setRecordId(res.data[0].id);
    }
  };

  return (
    <div className="home-content">
      <section className="home-card">
        <h2>FAQs Home Content</h2>
        <div className="home-field">
          <label className="home-label" htmlFor="faqs-title">Section Title</label>
          <input id="faqs-title" type="text" className="home-input" placeholder="A word from..." value={sectionTitle} onChange={(e) => setSectionTitle(e.target.value)} />
        </div>
        <div className="home-field">
          <label className="home-label" htmlFor="person-name">Person Name</label>
          <input id="person-name" type="text" className="home-input" placeholder="Parent Name" value={personName} onChange={(e) => setPersonName(e.target.value)} />
        </div>
        <div className="home-field">
          <label className="home-label" htmlFor="faqs-paragraph">Testimonial Quote</label>
          <textarea id="faqs-paragraph" className="home-textarea home-textarea--lg" value={testimonialQuote} onChange={(e) => setTestimonialQuote(e.target.value)} />
        </div>
        <div className="home-card__foot" style={{ marginTop: "1rem" }}>
          <button type="button" className="home-btn" onClick={save}>
            <FiSave aria-hidden /> Save
          </button>
        </div>
      </section>
    </div>
  );
}

const TAB_CONTENT = {
  "Hero Section": HeroSection,
  Services: ServicesSection,
  "Why This App": WhyThisAppSection,
  "AR Section": ARSection,
  Reviews: ReviewsSection,
  FAQs: FAQsSection,
};

function HomePage() {
  const [activeTab, setActiveTab] = useState("Hero Section");
  const ActivePanel = TAB_CONTENT[activeTab] ?? HeroSection;

  return (
    <>
      <SEO title="Home Page Management" description="Edit and manage Tilion website home page content." />
      <Sidebar />
      <div className="home-page">
        <main className="home-page__inner">
          <header className="home-head">
            <div>
              <h1>Home Page Management</h1>
              <p>Edit and manage your website&apos;s home page content</p>
            </div>
            <button type="button" className="home-btn">
              <FiSave aria-hidden />
              Publish Changes
            </button>
          </header>

          <nav className="home-tabs" aria-label="Home page sections">
            {TABS.map((tab) => (
              <button key={tab} type="button" className={`home-tab${activeTab === tab ? " active" : ""}`} onClick={() => setActiveTab(tab)}>
                {tab}
              </button>
            ))}
          </nav>

          <ActivePanel key={activeTab} />
        </main>
      </div>
    </>
  );
}

export default HomePage;